/* eslint frontbucket-patterns/no-new-sagas: "warn" */
import { uniqBy } from 'lodash-es';
import qs from 'qs';
import { RouterActionsType } from 'react-resource-router';
import { all, call, put, race, select, take } from 'redux-saga/effects';

import { User } from 'src/components/types';
import { BranchingModelState } from 'src/redux/branches/reducers/branch-list-reducer';
import { showFlag, showFlagComponent } from 'src/redux/flags';
import { RouteComponentProps } from 'src/router/types';
import {
  CREATE_BRANCH_ERROR_TYPE,
  CREATE_FROM,
  EXTENDED_BRANCH_KINDS,
} from 'src/sections/create-branch/constants';
import {
  getJiraIssueTypeForAnalytics,
  getRepositoryForIssueFromPrefsOrStorage,
  setRepositoryForIssueInLocalStorage,
} from 'src/sections/create-branch/jira';
import {
  CreateBranchError,
  CreateFromPayload,
  JiraIssue,
  Ref,
  CreateBranchParams,
  WorkflowBranches,
  BranchType,
  RefSelector,
  RepositoryDetailsMainBranch,
} from 'src/sections/create-branch/types';
import {
  generateBranchName,
  generateRefSelectorOptions,
  getRef,
  getRefs,
  getRepoFullName,
  getRepoOwnerAndSlug,
  moveMainBranchToTop,
  suggestFromBranch,
  isCreateBranchErrorDisplayedInline,
} from 'src/sections/create-branch/utils';
import { messages } from 'src/sections/global/components/flags/create-branch/create-branch-error-flag.i18n';
import { buildRefUrls } from 'src/sections/repository/sections/branches/hooks/utils';
import branchUrls from 'src/sections/repository/sections/branches/urls';
import repoUrls from 'src/sections/repository/urls';
import {
  getCurrentRepositoryOwnerName,
  getCurrentRepositorySlug,
  getRepositoryBranchingModel,
} from 'src/selectors/repository-selectors';
import { getCurrentUser } from 'src/selectors/user-selectors';
import { Action } from 'src/types/state';
import { escapeBbqlString } from 'src/utils/escape-bbql-string';
import { authRequest, jsonHeaders } from 'src/utils/fetch';
import {
  captureExceptionWithTags,
  captureMessageWithTags,
} from 'src/utils/sentry';

import {
  CHANGE_BRANCH_TYPE,
  changeFromBranch,
  ChangeRepositoryAction,
  closeCreateBranchGlobalDialog,
  closeCreateBranchRepoDialog,
  CREATE_BRANCH,
  FETCH_BRANCHING_MODEL,
  FETCH_COMMIT_STATUSES,
  FETCH_REF_OPTIONS,
  fetchBranchingModel,
  fetchRefOptions,
  FetchRefOptionsAction,
  FetchRefOptionsSuccessAction,
  LOAD_REPOSITORIES,
  LOAD_REPOSITORY,
  loadRepository,
  onChangeRepository,
  setSuggestedFromBranch,
} from '../actions';
import {
  getBranchTypesVisible,
  getCreateBranchParams,
  getJiraIssue,
  getRefSelectorState,
  getRepositorySelectorState,
  getSelectedBranchType,
  getSelectedRepoFullSlug,
  getSelectedRepoOwnerAndSlug,
  getSelectedRepositoryMainBranch,
  getWorkflowBranches,
} from '../selectors';

const getErrorType = (
  status: number,
  errorKey: string | null | undefined,
  message: string | null | undefined
): CREATE_BRANCH_ERROR_TYPE => {
  if (status >= 500 || !errorKey || !message) {
    return CREATE_BRANCH_ERROR_TYPE.GENERIC;
  }

  // @ts-ignore TODO: fix noImplicitAny error here
  return CREATE_BRANCH_ERROR_TYPE[errorKey] || CREATE_BRANCH_ERROR_TYPE.OTHER;
};

const findRepoMatchingTrimmedSearchFilter = (
  repositories: { name?: string; slug?: string }[],
  trimmedSearchFilter: string
): { name?: string; slug?: string } | undefined => {
  const filterLower = trimmedSearchFilter.toLowerCase();
  return repositories.find(
    repo =>
      repo.name?.toLowerCase() === filterLower ||
      repo.slug?.toLowerCase() === filterLower
  );
};

export function redirectToBranchPage(
  href: string,
  targetBranchName: string,
  push: RouteComponentProps['history']['push'] | RouterActionsType['push']
) {
  const queryParams = qs.stringify({ dest: targetBranchName });
  const relativeURL = `${new URL(href).pathname}?${queryParams}`;
  push(relativeURL);
}

export function* handleCreateBranchSuccessSaga(
  createFrom: CREATE_FROM,
  branch: any,
  targetBranchName: string,
  push: RouteComponentProps['history']['push'] | RouterActionsType['push']
) {
  // Once the branch page is a SPA, we will also
  // want to show the "create branch success" flag
  // here after redirecting the user.

  if (createFrom === CREATE_FROM.REPO_DIALOG) {
    yield put(closeCreateBranchRepoDialog());
  } else if (createFrom === CREATE_FROM.GLOBAL_DIALOG) {
    yield put(closeCreateBranchGlobalDialog());
  }

  // Store the preferred repository for the issue project
  if (
    [CREATE_FROM.GLOBAL_DIALOG, CREATE_FROM.GLOBAL_PAGE].includes(createFrom)
  ) {
    const issue: JiraIssue = yield select(getJiraIssue);
    if (issue) {
      const { selected } = yield select(getRepositorySelectorState);
      try {
        setRepositoryForIssueInLocalStorage(issue, selected.value);
      } catch {
        captureMessageWithTags('Failed to save the preferred repo for issue');
      }
    }
  }

  yield redirectToBranchPage(branch.links.html.href, targetBranchName, push);
}

export function* handleLoadingBranchingModelErrorSaga() {
  yield put({ type: FETCH_BRANCHING_MODEL.ERROR });
  /**
   *  this error flag is stateless and
   *  auto-dismiss disable
   * */
  yield put(
    showFlag({
      id: FETCH_BRANCHING_MODEL.ERROR,
      iconType: 'warning',
      title: { msg: messages.branchingModelsLoadingErrorTitle },
      description: { msg: messages.branchingModelsLoadingErrorDescription },
    })
  );
}
export function* handleCreateBranchErrorSaga(error: CreateBranchError) {
  yield put({
    type: CREATE_BRANCH.ERROR,
    payload: error,
  });
  // only show the error flag if the error is not one of the inline errors
  // that are displayed next to the branch name input field
  if (!isCreateBranchErrorDisplayedInline(error?.type)) {
    yield put(showFlagComponent('create-branch-error'));
  }
}

// @ts-ignore fix implicit any return type
export function* createBranchSaga(action: Action) {
  // eslint-disable-next-line prefer-destructuring
  const payload: CreateFromPayload = action.payload;
  const params: CreateBranchParams = yield select(getCreateBranchParams);
  const [owner, slug] = yield select(getSelectedRepoOwnerAndSlug);
  const branchType: BranchType | null = yield select(getSelectedBranchType);
  const branchTypeVisible: boolean = yield select(getBranchTypesVisible);
  const jiraIssue: JiraIssue = yield select(getJiraIssue);
  const url = repoUrls.api.v20.branches(owner, slug);
  const targetBranch = params.target;

  if (!targetBranch) {
    captureMessageWithTags(
      `Tried to create branch but could not find target branch data`
    );
    yield call(handleCreateBranchErrorSaga, {
      type: CREATE_BRANCH_ERROR_TYPE.BRANCH_PARAMS_MISSING,
    });
    return;
  }

  const body = JSON.stringify({
    name: generateBranchName(params.name, branchType),
    target: {
      hash: targetBranch.hash,
    },
  });

  const headers: HeadersInit = {
    ...jsonHeaders,
    'X-Bitbucket-ScreenName': payload.createFrom,
    // urlencode branch name because it can contain non-latin1 characters which are problematic in headers
    'X-Bitbucket-SourceBranch': encodeURIComponent(targetBranch.name),
  };

  if (branchTypeVisible) {
    headers['X-Bitbucket-BranchType'] = branchType
      ? branchType.kind
      : EXTENDED_BRANCH_KINDS.OTHER;
  }
  if (jiraIssue) {
    headers['X-Bitbucket-JiraIssueType'] =
      getJiraIssueTypeForAnalytics(jiraIssue);
  }

  const request = authRequest(url, {
    method: 'POST',
    body,
    headers,
  });

  try {
    const response: Response = yield call(fetch, request);
    if (response.ok) {
      // @ts-ignore
      const branch = yield response.json();
      yield call(
        handleCreateBranchSuccessSaga,
        action.payload.createFrom,
        branch,
        targetBranch.name,
        action.payload.push
      );
    } else {
      const { error } = yield response.json();
      const errorType = getErrorType(
        response.status,
        error.data && error.data.key,
        error.message
      );
      yield call(handleCreateBranchErrorSaga, {
        type: errorType,
        message: error.message,
      });
    }
  } catch (e) {
    captureExceptionWithTags(e);
    yield call(handleCreateBranchErrorSaga, {
      type: CREATE_BRANCH_ERROR_TYPE.GENERIC,
    });
  }
}

export function* fetchRefOptionsSaga({
  // @ts-ignore TODO: fix noImplicitAny error here
  payload: { search, mainBranch, repositoryFullSlug },
}) {
  // If a search query is not provided, we will fetch branches in one request
  // If a search query is provided, we will fetch branches in two requests
  // - First request fetches the exact match from the filter
  // - Second request fetches similar matches from the filter
  const urls: string[] = buildRefUrls(repositoryFullSlug, 'all', search || '');
  try {
    // Since branches can be fetched in 1 or 2 requests, we can use this object
    // to join the results for each request
    let res = { success: true, values: [], next: '' };
    for (const url of urls) {
      const request = authRequest(url);
      const response: Response = yield call(fetch, request);

      if (response.ok) {
        // @ts-ignore
        const result = yield response.json();

        // Combine each request's list of branches
        res.values = res.values.concat(result.values);

        // Store the last next url to populate hasMoreRefs
        res.next = result.next;
      } else {
        // If either request fails, we want to dispatch a FETCH_REF_OPTIONS.ERROR
        res = { success: false, values: [], next: '' };
        break;
      }
    }

    if (res.success) {
      let refs = getRefs(res.values);
      const branchingModel: BranchingModelState = yield select(
        getRepositoryBranchingModel
      );
      // @ts-ignore TODO: fix noImplicitAny error here
      const searchCondition = branch => {
        if (!search || !branch.name) {
          return true;
        }
        if (branch.name.includes(search)) {
          return true;
        }
        return false;
      };

      if (branchingModel) {
        const { development, production } = branchingModel;
        const priorityRefs: Ref[] = [development, production]
          .filter(b => b && b.use_mainbranch && searchCondition(b))
          .map(
            b =>
              ({
                hash: b && b.target ? b.target.hash : undefined,
                name: b && b.name ? b.name : '',
                type: b && b.type.toUpperCase(),
              } as Ref)
          );
        const uniqPriorityRefs = uniqBy(priorityRefs, 'name');
        const priorityRefNames = uniqPriorityRefs.map(b => b.name);
        const filteredRefs = refs.filter(
          b => !priorityRefNames.includes(b.name)
        );

        refs = [...uniqPriorityRefs, ...filteredRefs];
      }

      const reorderedRefs = moveMainBranchToTop(refs, mainBranch, !!search);
      const result: FetchRefOptionsSuccessAction = {
        type: FETCH_REF_OPTIONS.SUCCESS,
        payload: {
          mainRef: mainBranch ? getRef(mainBranch) : null,
          refs: generateRefSelectorOptions(reorderedRefs),
          hasMoreRefs: !!res.next,
        },
      };
      yield put(result);
    } else {
      yield put({
        type: FETCH_REF_OPTIONS.ERROR,
      });
    }
  } catch (e) {
    captureExceptionWithTags(e);
    yield put({
      type: FETCH_REF_OPTIONS.ERROR,
    });
  }
}

export function* selectDataAndFetchRefOptionsSaga({
  payload: { search },
}: FetchRefOptionsAction) {
  const repositoryFullSlug: string = yield select(getSelectedRepoFullSlug);
  const mainBranch: RepositoryDetailsMainBranch = yield select(
    getSelectedRepositoryMainBranch
  );
  yield call(fetchRefOptionsSaga, {
    payload: {
      repositoryFullSlug,
      mainBranch,
      search,
    },
  });
}

// @ts-ignore fix implicit any return type
export function* fetchCommitStatusesSaga() {
  const [owner, slug] = yield select(getSelectedRepoOwnerAndSlug);
  const {
    target: { hash },
  } = yield select(getCreateBranchParams);
  const url = branchUrls.api.v20.commitStatuses(owner, slug, hash);
  const request = authRequest(url);
  try {
    const response: Response = yield call(fetch, request);
    if (response.ok) {
      const { values: statuses } = yield response.json();
      yield put({
        type: FETCH_COMMIT_STATUSES.SUCCESS,
        payload: statuses,
      });
    } else {
      yield put({
        type: FETCH_COMMIT_STATUSES.ERROR,
      });
    }
  } catch (e) {
    captureExceptionWithTags(e);
    yield put({
      type: FETCH_COMMIT_STATUSES.ERROR,
    });
  }
}

// @ts-ignore fix implicit any return type
export function* fetchBranchingModelSaga() {
  const [owner, slug] = yield select(getSelectedRepoOwnerAndSlug);
  try {
    const url = repoUrls.api.v20.branchingModel(owner, slug);
    const request = authRequest(url);
    const response: Response = yield call(fetch, request);
    if (response.ok) {
      const {
        development,
        production,
        branch_types: branchTypes,
      } = yield response.json();

      const devBranch = development && development.branch;
      const prodBranch = production && production.branch;

      yield put({
        type: FETCH_BRANCHING_MODEL.SUCCESS,
        payload: {
          development: devBranch ? getRef(devBranch) : null,
          production: prodBranch ? getRef(prodBranch) : null,
          branchTypes,
        },
      });
    } else {
      yield call(handleLoadingBranchingModelErrorSaga);
    }
  } catch (e) {
    captureExceptionWithTags(e);
    yield call(handleLoadingBranchingModelErrorSaga);
  }
}

export function* handleChangeRepositorySaga(action: ChangeRepositoryAction) {
  const {
    payload: { selected },
  } = action;
  const [owner, slug] = getRepoOwnerAndSlug(selected.value);
  yield put(loadRepository(owner, slug));
  yield take([LOAD_REPOSITORY.SUCCESS, LOAD_REPOSITORY.ERROR]);

  yield put(fetchRefOptions());
  yield put(fetchBranchingModel());
}

export function* setCurrentRepositorySaga() {
  const owner: string = yield select(getCurrentRepositoryOwnerName);
  const slug: string = yield select(getCurrentRepositorySlug);
  const fullName = getRepoFullName({ owner, slug });

  yield put(
    onChangeRepository({ value: fullName, label: fullName, data: undefined })
  );
}

export function* loadRepositoriesWithExactMatchSaga(
  action: Action<{ url: string; workspaceSlug: string; trimmedFilter?: string }>
) {
  const payloadIn = action.payload;
  if (!payloadIn) return;
  const { url, workspaceSlug, trimmedFilter } = payloadIn;

  try {
    // Perform fuzzy search first
    const fuzzySearchRequest = authRequest(url);
    const fuzzySearchResponse: Response = yield call(fetch, fuzzySearchRequest);

    if (!fuzzySearchResponse.ok) {
      yield put({
        type: LOAD_REPOSITORIES.ERROR,
        payload: fuzzySearchResponse.status,
      });
      return;
    }

    let fuzzySearchPayload: {
      values?: any[];
      repositories?: any[];
      size?: number;
    } = yield call([fuzzySearchResponse, fuzzySearchResponse.json]);
    const fuzzySearchResults = fuzzySearchPayload?.values ?? [];

    if (trimmedFilter) {
      const repoMatchingSearchFilter = findRepoMatchingTrimmedSearchFilter(
        fuzzySearchResults,
        trimmedFilter
      );
      // If fuzzy search didn't find a repo with a matching name, perform exact search
      if (!repoMatchingSearchFilter) {
        try {
          const getByNameExactlyQuery = `name="${escapeBbqlString(
            trimmedFilter
          )}"`;
          const getByNameExactlyUrl = `/!api/2.0/repositories/${workspaceSlug}?${qs.stringify(
            {
              q: getByNameExactlyQuery,
              role: 'contributor',
              sort: 'name',
            }
          )}`;
          const getByNameExactlyRequest = authRequest(getByNameExactlyUrl);
          const getByNameExactlyResponse: Response = yield call(
            fetch,
            getByNameExactlyRequest
          );
          if (getByNameExactlyResponse.ok) {
            // @ts-ignore TODO: fix noImplicitAny error here
            const geyByNameExactlyPayload: {
              values?: any[];
            } = yield call([
              getByNameExactlyResponse,
              getByNameExactlyResponse.json,
            ]);
            const geyByNameExactlyValues =
              geyByNameExactlyPayload?.values ?? [];
            if (geyByNameExactlyValues.length > 0) {
              const key = fuzzySearchPayload.values ? 'values' : 'repositories';
              const list = fuzzySearchPayload[key] ?? [];
              fuzzySearchPayload = {
                ...fuzzySearchPayload,
                [key]: [geyByNameExactlyValues[0], ...list],
              };
            }
          }
        } catch {
          captureMessageWithTags('Failed to perform exact repository search');
        }
      }
    }

    yield put({
      type: LOAD_REPOSITORIES.SUCCESS,
      payload: fuzzySearchPayload,
      meta: { workspaceSlug, searchFilter: trimmedFilter },
    });
  } catch (error) {
    yield put({
      type: LOAD_REPOSITORIES.ERROR,
      payload: error instanceof Error ? error.message : String(error),
      error: true,
    });
  }
}

export function* handleLoadRepositoriesSuccessSaga({ payload, meta }: Action) {
  const repositories =
    payload.repositories || // old payload using repositories key
    (Array.isArray(payload) ? payload : payload?.values || []); // new payload using values key

  if (repositories && repositories.length !== 0) {
    const issue: JiraIssue = yield select(getJiraIssue);
    let preferredRepoForIssue = null;
    if (issue) {
      const currentUser: User = yield select(getCurrentUser);
      const repoNameForIssue: string | null = yield call(
        getRepositoryForIssueFromPrefsOrStorage,
        issue,
        currentUser
      );

      preferredRepoForIssue =
        repoNameForIssue &&
        // @ts-ignore TODO: fix noImplicitAny error here
        repositories.find(r => r.full_name === repoNameForIssue);

      // If the stored repo is not in the list but matches the selected workspace, try to fetch it directly.
      // If the stored repo is from a different workspace, don't fetch it.
      if (repoNameForIssue && !preferredRepoForIssue) {
        // Arbitrarily selecting the first repository to get the selected workspace slug
        // This assumes all repositories in the list are from the same workspace
        const selectedWorkspace = repositories[0]?.workspace?.slug;
        const workspaceForIssue = repoNameForIssue.split('/')[0];
        const shouldFetch =
          !selectedWorkspace || workspaceForIssue === selectedWorkspace;

        if (shouldFetch) {
          try {
            const [workspace, slug] = repoNameForIssue.split('/');
            if (workspace && slug) {
              const url = `/!api/2.0/repositories/${workspace}/${slug}`;
              const request = authRequest(url);
              const response: Response = yield call(fetch, request);
              if (response.ok) {
                // @ts-ignore TODO: fix noImplicitAny error here
                const storedRepo = yield call([response, response.json]);
                preferredRepoForIssue = storedRepo;
              }
            }
          } catch {
            captureMessageWithTags(
              'Failed to fetch the preferred repo for issue'
            );
          }
        }
      }
    }

    // Prefer exact match when user typed a search filter
    // loadRepositoriesWithExactMatchSaga may have prepended it to the list
    const trimmedSearchFilter = meta?.searchFilter?.trim();
    const repoMatchingExactSearch =
      trimmedSearchFilter &&
      findRepoMatchingTrimmedSearchFilter(repositories, trimmedSearchFilter);

    // When the user actively searched, prefer the exact match over stored preference.
    // On initial load (no search filter), prefer the stored preference for the Jira issue.
    const selectRepoForActiveSearch = () =>
      repoMatchingExactSearch || preferredRepoForIssue || repositories[0];
    const selectRepoForInitialLoad = () =>
      preferredRepoForIssue || repositories[0];

    const defaultRepo = trimmedSearchFilter
      ? selectRepoForActiveSearch()
      : selectRepoForInitialLoad();

    yield put(
      onChangeRepository({
        value: defaultRepo.full_name,
        label: defaultRepo.slug,
        data: undefined,
      })
    );
  }
}

export function* handleLoadRepositoriesErrorSaga() {
  yield put(showFlagComponent('create-branch-load-repositories-error'));
}

export function* suggestFromBranchSaga() {
  while (true) {
    // We need to trigger pre-selection in the following cases:
    //   - repository refs were successfully fetched AND
    //     branching model was fetched successfully or with an error
    //   - branch type was changed
    yield race({
      changeBranchType: take(CHANGE_BRANCH_TYPE),
      fetchBranchAndRefs: all([
        race({
          branchModelSuccess: take(FETCH_BRANCHING_MODEL.SUCCESS),
          branchModelFailure: take(FETCH_BRANCHING_MODEL.ERROR),
        }),
        take(FETCH_REF_OPTIONS.SUCCESS),
      ]),
    });

    const workflowBranches: WorkflowBranches = yield select(
      getWorkflowBranches
    );
    const branchType: BranchType = yield select(getSelectedBranchType);

    const fromBranch = suggestFromBranch(workflowBranches, branchType);
    if (fromBranch) {
      yield put(setSuggestedFromBranch(fromBranch));

      const refSelector: RefSelector = yield select(getRefSelectorState);
      // Change from branch only if user hasn't changed it already
      if (!refSelector.selectedByUser) {
        yield put(changeFromBranch(fromBranch, false));
      }
    }
  }
}
